import { memo, useEffect, useRef, useState, type DragEvent as ReactDragEvent, type ReactElement } from 'react';
import type { CapabilityManifest, BillPayDraft } from '@lapo/core';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * The ✦ assistant palette (right-drawer panel). A tiny chat over the `/assistant` router: the user
 * types one natural-language line, the server classifies it (add a task / save a note / draft a
 * shipping label) and does the work, and the turn is echoed back here. Presentational — the host
 * owns the message list and the `onSend` side effects (refresh Today, jump to Shipments, etc.).
 */
export type AssistantTool = 'add_task' | 'add_note' | 'create_shipping_label' | 'track_package' | 'place_call' | 'pay_bill' | 'none';

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** which action the assistant took (assistant turns only) — drives the row icon */
  tool?: AssistantTool;
  /** false = nothing was done (a clarification), so the row is styled as a prompt, not a success */
  ok?: boolean;
  /** REMIT-CHAT — a drafted bill payment rendered as a confirm-first Pay button on this message. */
  billDraft?: BillPayDraft;
}

export interface AssistantPanelProps {
  messages: AssistantMessage[];
  onSend: (prompt: string) => void;
  /** cancel the in-flight request (the Stop button, shown in place of Send while busy) */
  onStop?: () => void;
  /** wipe the conversation (the sweep button, or typing "clear") */
  onClear?: () => void;
  /** dismiss the panel — renders an explicit ✕ in the header (the only obvious exit on phones). */
  onClose?: () => void;
  busy?: boolean;
  /** OPENCLAW streaming — live one-line progress ("Searching your email…") shown while busy. */
  busyStatus?: string;
  error?: string;
  /** when set, the composer is disabled and this explains why (e.g. API not configured) */
  disabledReason?: string;
  /**
   * REMIT-CHAT — the user SENT a message with a staged bill image (paperclip/paste stages it in the
   * composer first; nothing uploads until they hit Send, like attaching a file in Claude/ChatGPT).
   * `prompt` is whatever text accompanied it ('' when the image was sent alone).
   */
  onSendImage?: (file: File, prompt: string) => void;
  /** REMIT-CHAT — the user tapped Pay on a drafted bill; run the real (guarded) payment. */
  onConfirmPay?: (billId: string) => void;
  /**
   * NEXUS-07: the per-user capability manifest (GET /me/capabilities). When present, the empty state
   * renders "what Lapo can do for you" from it — the same source of truth the assistant reads — so the
   * user, the UI, and the model agree. Absent ⇒ the static example prompts are shown instead.
   */
  capabilities?: CapabilityManifest;
}

const EXAMPLES = [
  'Add a task to email the printer tomorrow',
  'Note: ideas for the launch page',
  'Ship a 2 lb box to Jane in Austin, TX',
];

function iconFor(tool: AssistantTool | undefined): string {
  switch (tool) {
    case 'add_task':
      return 'task_alt';
    case 'add_note':
      return 'note_add';
    case 'create_shipping_label':
      return 'local_shipping';
    case 'track_package':
      return 'my_location';
    case 'place_call':
      return 'call';
    case 'pay_bill':
      return 'receipt_long';
    default:
      return 'auto_awesome';
  }
}

/** Confirm-first Pay button rendered on an assistant message that drafted a bill payment. */
function PayDraft({ draft, onConfirm }: { draft: BillPayDraft; onConfirm?: (billId: string) => void }): ReactElement | null {
  if (!draft.payable) return null;
  const paying = draft.status === 'approved' || draft.status === 'paying';
  const paid = draft.status === 'paid';
  return (
    <div className="lapo-assistant-paydraft">
      <button
        type="button"
        className="lapo-set-btn is-primary"
        disabled={paying || paid || !onConfirm}
        onClick={() => onConfirm?.(draft.billId)}
      >
        {paid ? 'Paid ✓' : paying ? 'Paying…' : `Pay ${draft.amount}${draft.last4 ? ` · •${draft.last4}` : ''}`}
      </button>
      {/* DOCKET D19 — link to the bill's ledger detail. A plain hash link, so it works on every
          surface without threading a navigation callback through the panel. */}
      <a className="lapo-assistant-paydraft-view" href={`#/bills/${encodeURIComponent(draft.billId)}`}>
        View bill
      </a>
    </div>
  );
}

// Memoized (audit F3). Hoisted so the export can wrap it above the definition.
export const AssistantPanel = memo(AssistantPanelBase);

function AssistantPanelBase({ messages, onSend, onStop, onClear, onClose, busy, busyStatus, error, disabledReason, capabilities, onSendImage, onConfirmPay }: AssistantPanelProps): ReactElement {
  const [prompt, setPrompt] = useState('');
  // A staged (not yet sent) bill image. It rides along when the user hits Send — attaching alone
  // does NOT submit, so you can attach and type "pay this" as one message.
  const [attachment, setAttachment] = useState<File | null>(null);
  // True while a file is dragged over the composer — paints the drop affordance.
  const [dragging, setDragging] = useState(false);
  const disabled = !!disabledReason;
  const fileRef = useRef<HTMLInputElement>(null);

  const canAttach = !!onSendImage && !disabled && !busy;
  // Drag-drop an image straight onto the composer — the drop STAGES it (like the paperclip / paste), it
  // isn't sent until the user hits Send. Only images are supported (the assistant reads bill images), so
  // a non-image drop is a no-op here; we still preventDefault so the browser never opens the file.
  const onComposeDrop = (e: ReactDragEvent): void => {
    if (!canAttach) return;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (!files.length) return;
    e.preventDefault();
    setDragging(false);
    const img = files.find((f) => f.type.startsWith('image/'));
    if (img) setAttachment(img);
  };
  const onComposeDragOver = (e: ReactDragEvent): void => {
    if (!canAttach || !Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragging(true);
  };

  // Keep the log pinned to the newest message as the conversation grows, so you always see the latest
  // reply without scrolling. `stick` stays true while you're near the bottom; if you scroll up to read
  // history it flips off and we stop auto-scrolling until you return to the bottom.
  const logRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = logRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages, busy, error]);
  const onLogScroll = (): void => {
    const el = logRef.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };

  const submit = (): void => {
    const p = prompt.trim();
    if ((!p && !attachment) || busy || disabled) return;
    // "clear" is a shortcut to wipe the chat (mirrors the sweep button).
    if (!attachment && p.toLowerCase() === 'clear' && onClear) {
      onClear();
      setPrompt('');
      return;
    }
    if (attachment && onSendImage) {
      onSendImage(attachment, p);
      setAttachment(null);
    } else {
      onSend(p);
    }
    setPrompt('');
  };

  return (
    <section className="lapo-assistant" data-testid="assistant-panel">
      {(onClose || (messages.length > 0 && onClear)) && (
        <div className="lapo-assistant-bar">
          {messages.length > 0 && onClear && (
            <button
              type="button"
              className="lapo-assistant-clear"
              aria-label="Clear chat"
              title="Clear chat"
              onClick={onClear}
            >
              <MaterialIcon name="delete_sweep" size={18} />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="lapo-assistant-clear"
              aria-label="Close assistant"
              title="Close"
              onClick={onClose}
            >
              <MaterialIcon name="close" size={18} />
            </button>
          )}
        </div>
      )}
      <div className="lapo-assistant-log" ref={logRef} onScroll={onLogScroll}>
        {messages.length === 0 ? (
          <div className="lapo-assistant-empty">
            <MaterialIcon name="auto_awesome" size={22} />
            <p className="lapo-assistant-empty-title">Ask Lapo to do something</p>
            {(() => {
              // NEXUS-07: render "what Lapo can do for you" from the capability manifest when available —
              // one source of truth shared with the model. Fall back to static examples otherwise.
              const offered = (capabilities ?? []).filter((c) => c.offered);
              if (offered.length) {
                return (
                  <ul className="lapo-assistant-capabilities">
                    {offered.map((c) => (
                      <li key={c.id} className="lapo-assistant-capability">
                        <span className="lapo-assistant-capability-name">{c.name}</span>
                        <span className="lapo-assistant-capability-summary">{c.aiSummary}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <ul className="lapo-assistant-examples">
                  {EXAMPLES.map((e) => (
                    <li key={e}>
                      <button
                        type="button"
                        className="lapo-assistant-example"
                        disabled={disabled || busy}
                        onClick={() => onSend(e)}
                      >
                        {e}
                      </button>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`lapo-assistant-msg lapo-assistant-${m.role}${m.ok === false ? ' is-none' : ''}`}
            >
              {m.role === 'assistant' && <MaterialIcon name={iconFor(m.tool)} size={16} />}
              <div className="lapo-assistant-msg-body">
                <span>{m.text}</span>
                {m.billDraft && <PayDraft draft={m.billDraft} onConfirm={onConfirmPay} />}
              </div>
            </div>
          ))
        )}
        {busy && (
          <div className="lapo-assistant-msg lapo-assistant-assistant is-busy">
            <MaterialIcon name="auto_awesome" size={16} />
            {/* OPENCLAW streaming — narrate the agent's live tool work instead of a mute spinner. */}
            <span>{busyStatus ?? 'Working…'}</span>
          </div>
        )}
        {error && (
          <div className="lapo-assistant-error" role="alert">
            {error}
          </div>
        )}
      </div>

      {/* data-lapo-file-drop-ignore: the app-wide FileDropOverlay skips drops that land here, so this
          composer owns image drops instead of the overlay attaching them to the note behind the drawer. */}
      <div
        className={`lapo-assistant-compose${dragging ? ' is-dragging' : ''}`}
        data-lapo-file-drop-ignore
        onDragOver={canAttach ? onComposeDragOver : undefined}
        onDragLeave={canAttach ? () => setDragging(false) : undefined}
        onDrop={canAttach ? onComposeDrop : undefined}
      >
        {dragging && (
          <div className="lapo-assistant-dropveil" aria-hidden>
            <MaterialIcon name="image" size={20} />
            <span>Drop an image to attach</span>
          </div>
        )}
        {attachment && (
          <div className="lapo-assistant-attachment" data-testid="assistant-attachment">
            <MaterialIcon name="image" size={16} />
            <span className="lapo-assistant-attachment-name">{attachment.name || 'bill image'}</span>
            <button
              type="button"
              className="lapo-assistant-attachment-remove"
              aria-label="Remove attachment"
              title="Remove attachment"
              onClick={() => setAttachment(null)}
            >
              <MaterialIcon name="close" size={14} />
            </button>
          </div>
        )}
        <div className="lapo-assistant-field">
          {onSendImage && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setAttachment(file); // staged — sent with the message, not on select
                  e.target.value = ''; // allow re-selecting the same file
                }}
              />
              <button
                type="button"
                className="lapo-assistant-attach"
                aria-label="Attach a bill image"
                title="Attach a bill image to pay"
                disabled={disabled || busy}
                onClick={() => fileRef.current?.click()}
              >
                <MaterialIcon name="attach_file" size={18} />
              </button>
            </>
          )}
          <textarea
            className="lapo-assistant-input"
            aria-label="Ask the assistant"
            rows={2}
            spellCheck={false}
            value={prompt}
            placeholder={disabledReason ?? 'Add a task, save a note, or draft a shipping label…'}
            disabled={disabled}
            onChange={(e) => setPrompt(e.target.value)}
            onPaste={(e) => {
              // Paste a bill image straight from the clipboard (screenshot / copied photo) — same path as
              // the paperclip: it's STAGED in the composer, sent when the user hits Send. Only intercept
              // when an image is present so pasting text stays untouched.
              if (!onSendImage || disabled || busy) return;
              const img = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
              if (img) {
                e.preventDefault();
                setAttachment(img);
              }
            }}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter inserts a newline (a multi-line request).
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          {busy && onStop ? (
            <button
              type="button"
              className="lapo-set-btn is-primary lapo-assistant-send"
              aria-label="Stop"
              title="Stop"
              onClick={onStop}
            >
              <MaterialIcon name="stop" size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="lapo-set-btn is-primary lapo-assistant-send"
              aria-label="Send"
              title={disabledReason}
              disabled={disabled || busy || (!prompt.trim() && !attachment)}
              onClick={submit}
            >
              <MaterialIcon name="arrow_upward" size={18} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
